import DeleteIcon from "@mui/icons-material/Delete";
import { useEffect, useState } from "react";
import { FieldValues, FormProvider, useForm, UseFormGetValues } from "react-hook-form";
import { UseFormReset } from "react-hook-form/dist/types/form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { INFORMATION, OPENING_TIMES } from "../../Layout/Tabs/MdmTabPanelConstants";
import PageHeader from "../../Layout/Header/PageHeader";
import PageHeaderButton from "../../Layout/Header/PageHeaderButton";
import PageHeaderIconButton from "../../Layout/Header/PageHeaderIconButton";
import { MdmTabs } from "../../Layout/Tabs/MdmTabs";
import Odc from "../Odc";
import { odcDefaultValues } from "./odcDefaultValues";
import { OdcForm } from "./OdcForm";
import { confirm } from "../../Layout/Dialog/ConfirmDialog";
import { OdcHandlingTimesForm } from "./HandlingTimes/OdcHandlingTimesForm";
import { isFormSubmitForbidden } from "../../Forms/isFormSubmitForbidden";
import { OdcType } from "../OdcType";
import { odcSchema } from "./odcSchema";
import { OdcOpeningTimesParentForm } from "./OpeningTimes/OdcOpeningTimesParentForm";
import { useIsUserInRoles } from "../../Auth/useIsUserInRoles";
import { AuthorizationPolicies } from "../../Auth/AuthorizationPolicy";

type Props = {
  title: string;
  odc: Odc | undefined;
  onSave: (odc: Odc) => void;
  onDelete?: (odc: Odc) => void;
  disabledFields?: Array<string> | undefined;
};

export default function OdcFormTabs({ title, odc, onSave, onDelete, disabledFields }: Props) {
  const isUserInAdminNetworkRole = useIsUserInRoles(AuthorizationPolicies.RequireAdminNetworkRoles);

  const methods = useForm<Odc>({
    resolver: yupResolver(odcSchema),
    defaultValues: odcDefaultValues,
    mode: "onChange",
    disabled: !isUserInAdminNetworkRole,
  });

  useEffect(() => {
    methods.reset(odc);
    setHandlingTimeTabDisabled(odc?.Type !== OdcType.ODC);
    return () => methods.reset(odcDefaultValues);
  }, [odc, methods.reset, methods, odc?.Type]);

  const onDeleteWithConfirmation = async (values: Odc) => {
    const isConfirmed = await confirm(`Do you really want to delete ODC with Id ${odc?.Id}?`);
    if (!isConfirmed || !onDelete) {
      return;
    }
    onDelete(values);
  };

  const resetFormStateAndKeepFormValues = <TFieldValues extends FieldValues>(
    resetFromContext: UseFormReset<TFieldValues>,
    getValuesFromContext: UseFormGetValues<TFieldValues>
  ) => {
    const formValues = getValuesFromContext();
    resetFromContext(formValues, { keepValues: true });
  };

  const [handlingTimeTabDisabled, setHandlingTimeTabDisabled] = useState<boolean>(false);
  const disableHandlingTimeTab = (odcType: unknown) => {
    setHandlingTimeTabDisabled(odcType !== OdcType.ODC);
  };

  return (
    <div style={{ height: "calc(100vh - 64px)" }}>
      <PageHeader headerText={title}>
        {onDelete !== undefined && (
          <PageHeaderIconButton
            onClick={methods.handleSubmit((it) => onDeleteWithConfirmation(it))}
            color="secondary"
            disabled={!isUserInAdminNetworkRole}
          >
            <DeleteIcon />
          </PageHeaderIconButton>
        )}
        <PageHeaderButton
          onClick={methods.handleSubmit((values: Odc) => {
            onSave(values);
            resetFormStateAndKeepFormValues(methods.reset, methods.getValues);
          })}
          disabled={isFormSubmitForbidden(methods.formState)}
        >
          Save
        </PageHeaderButton>
      </PageHeader>

      <FormProvider {...methods}>
        <MdmTabs
          tabs={[
            {
              index: 0,
              label: INFORMATION,
              content: (
                <OdcForm
                  disabledFields={disabledFields}
                  onOdcTypeChanged={(it) => disableHandlingTimeTab(it)}
                />
              ),
            },
            {
              index: 1,
              label: "HANDLING TIMES",
              content: (
                <OdcHandlingTimesForm disabledFields={disabledFields} odcId={odc?.Id ?? 0} />
              ),
              disabled: handlingTimeTabDisabled || !odc,
            },
            {
              index: 2,
              label: OPENING_TIMES,
              content: <OdcOpeningTimesParentForm parentId={odc?.Id ?? 0} />,
              disabled: !odc,
            },
          ]}
        />
      </FormProvider>
    </div>
  );
}
